import React from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Grid, Header, Segment, Table, Button, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import Energy from './Energy'
import InfoPopup from '~/components/InfoPopup'
import FieldOperation from './FieldOperation'
import WasteWater from './WasteWater'

const FuelEnergy: React.FC = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const { fields: energyFields, append: appendEnergy, remove: removeEnergy } = useFieldArray({
    control,
    name: 'energyTypes',
    keyName: 'key'
  })

  const { fields: fieldOpFields, append: appendFieldOp, remove: removeFieldOp } = useFieldArray({
    control,
    name: 'fieldOperations',
    keyName: 'key'
  })

  function renderFieldOperationHeader(index) {
    return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
      <Header as='h4'>{t('Field operation')} {index + 1}</Header>
      <Icon
        name='trash'
        color='red'
        size='large'
        style={{ cursor: 'pointer' }}
        onClick={() => removeFieldOp(index)}
      />
    </div>
  }

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5 }}>
              <div>{t('Direct energy use')}</div>
            </div>
          </Header>
          <Segment attached>
            <div>{t('Enter data for electricity and fuels used for crop production and on-farm processing.')}</div>
            <div>{t('If you enter all of your in field energy in Direct energy use, then you should skip section Field Operations to prevent double-counting.')}</div>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t('Energy source')}</Table.HeaderCell>
                  <Table.HeaderCell>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <div>{t('Energy used')}</div>
                      <InfoPopup
                        content={t('Data is for energy use over the entire field NOT per hectare (or per acre).')}
                        styleIcon={{ fontSize: '1.2em', color: 'white' }}
                      />
                    </div>
                  </Table.HeaderCell>
                  <Table.HeaderCell>{t('Unit')}</Table.HeaderCell>
                  <Table.HeaderCell>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <div>{t('Category')}</div>
                      <InfoPopup
                        content={t(`Select whether this refers to energy used for field operations, or for processing.
                            If you do not have actual data for fuel used for all of your field operations, then you can add them as individual estimates in each of the sections below.`)}
                        styleIcon={{ fontSize: '1.2em', color: 'white' }}
                      />
                    </div>
                  </Table.HeaderCell>
                  <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {energyFields.map((field, index) => (
                  <Table.Row key={field.key}>
                    <Energy
                      index={index}
                      removeEnergy={removeEnergy}
                    />
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
              <Button
                type='button'
                icon='plus'
                style={{ color: 'white' }}
                content={t('Add Energy Type')}
                onClick={() => appendEnergy({
                  id: null,
                  amount: null,
                  unit: null,
                  category: null
                })}
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5 }}>
              <div>{t('Field Operations')}</div>
              <InfoPopup
                content={t('If data on actual fuel used for field operations is not available, then use this section to generate average estimates of energy used for particular types of field operations.')}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <div>{t('Use this estimator for any individual field operations not captured in Direct energy use, above.')}</div>
            {fieldOpFields.map((field, index) => (
              <Segment key={field.key}>
                {renderFieldOperationHeader(index)}
                <FieldOperation
                  index={index}
                />
              </Segment>
            ))}
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
              <Button
                type='button'
                icon='plus'
                style={{ color: 'white' }}
                content={t('Add Field Operation')}
                onClick={() => appendFieldOp({
                  fuelType: null,
                  numberOfOperations: null
                })}
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5 }}>
              <div>{t('Waste water emissions')}</div>
              <InfoPopup
                content={t('Waste water includes run off from land, livestock, waste management (such as slurry), and any cleaning, washing or processing within the scope of your assessment.')}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <WasteWater />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default FuelEnergy
